// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import * as Location from 'expo-location';
import { useEffect, useState } from 'react';

import { reportFixQuality, startGnssQualityWatch } from '@/services/gnssQuality';
import { combineFixes, isStationary, type ClusterFix } from '@/services/positionCluster';

import type { LatLon } from './tileMath';

export type PlayerPosition = LatLon & {
  /** Horizontal accuracy in metres, or null when the platform withholds it.
   *  While averaging this is the standard error of the combined fix, so it is
   *  smaller than any single reported accuracy — the accuracy ring shrinks. */
  accuracy: number | null;
  /** How many fixes went into this position. 1 means a raw single fix. */
  sampleCount: number;
  /** Whether this is an averaged standing position rather than a live fix. */
  averaged: boolean;
};

export type PlayerPositionStatus =
  /** Not asked for — the view is closed. */
  | 'idle'
  /** Permission granted, no usable fix yet. */
  | 'locating'
  | 'tracking'
  | 'denied'
  /** No location provider, or it failed outright. */
  | 'unavailable';

export type PlayerPositionState = {
  position: PlayerPosition | null;
  status: PlayerPositionStatus;
};

/** Fixes older than this have left the standing cluster. */
const CLUSTER_WINDOW_MS = 8000;

/** Hard cap on cluster size; at one fix a second the window binds first. */
const MAX_CLUSTER_FIXES = 12;

/** This view watches at 1 Hz, so "the newest fix still describes now" is a much
 *  tighter window here than the shot-tracking default built for 3 s sampling. */
const CLUSTER_FRESHNESS_MS = 3000;

const toClusterFix = (fix: Location.LocationObject, now: number): ClusterFix => ({
  lat: fix.coords.latitude,
  lon: fix.coords.longitude,
  accuracy: fix.coords.accuracy ?? null,
  speed: fix.coords.speed ?? null,
  ts: fix.timestamp || now,
});

const rawPosition = (fix: Location.LocationObject): PlayerPosition => ({
  lat: fix.coords.latitude,
  lon: fix.coords.longitude,
  accuracy: fix.coords.accuracy ?? null,
  sampleCount: 1,
  averaged: false,
});

/**
 * The player's live position, for as long as the hole view is open.
 *
 * Deliberately scoped to the open view rather than the round: continuous positioning is the
 * most expensive thing a golf app does to a battery, and this is a screen a player looks at
 * for a few seconds a hole. Course-mapping capture ({@link useCourseTrace}) is the separate,
 * consent-gated thing that runs all round.
 *
 * Every failure — permission refused, no provider, a throwing platform — resolves to a status
 * and never to an exception, because the view is still useful without a position: it simply
 * measures from the tee instead.
 *
 * While the player stands still the reported position is the average of the standing cluster
 * rather than the newest fix ({@link combineFixes}) — the only accuracy the app can buy in
 * software, and worth buying on the one screen that quotes a distance to the metre. The
 * moment the player walks, the cluster is discarded and raw fixes take over: a lagging
 * position while moving would be a worse lie than a jittery one.
 */
export const usePlayerPosition = ({ enabled }: { enabled: boolean }): PlayerPositionState => {
  const [state, setState] = useState<PlayerPositionState>({ position: null, status: 'idle' });

  useEffect(() => {
    if (!enabled) {
      setState({ position: null, status: 'idle' });
      return undefined;
    }

    let cancelled = false;
    let subscription: Location.LocationSubscription | null = null;
    // Scoped to this effect run, so closing the view or losing permission starts
    // the next session with an empty cluster rather than yesterday's spot.
    let cluster: ClusterFix[] = [];
    // Observe the constellation for as long as we watch position, so each fix can
    // be tagged with whether SBAS and L5 were in play.
    const stopGnssWatch = startGnssQualityWatch();

    const begin = async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (cancelled) return;
        if (status !== 'granted') {
          setState({ position: null, status: 'denied' });
          return;
        }

        setState((current) => ({ ...current, status: 'locating' }));

        subscription = await Location.watchPositionAsync(
          {
            // Golf distances are quoted to the metre, so this is the one place in the app
            // that asks for the best fix the device can give.
            accuracy: Location.Accuracy.BestForNavigation,
            timeInterval: 1000,
            distanceInterval: 1,
          },
          (fix) => {
            if (cancelled) return;

            const now = Date.now();
            const candidate = [
              ...cluster.filter((member) => now - member.ts <= CLUSTER_WINDOW_MS),
              toClusterFix(fix, now),
            ].slice(-MAX_CLUSTER_FIXES);

            const standing = isStationary(candidate, now, CLUSTER_FRESHNESS_MS);
            // Walking restarts the cluster from the newest fix: averaging across a
            // walked line would report a spot the player has already left.
            cluster = standing ? candidate : candidate.slice(-1);

            const combined = standing ? combineFixes(cluster) : null;
            const position = combined
              ? { ...combined, averaged: combined.sampleCount > 1 }
              : rawPosition(fix);

            reportFixQuality({
              accuracyMeters: position.accuracy,
              sampleCount: position.sampleCount,
              now,
            });

            setState({ position, status: 'tracking' });
          }
        );

        // The watch can take several seconds outdoors and forever indoors; the last known
        // fix fills the gap so the view opens with a distance rather than a spinner.
        const last = await Location.getLastKnownPositionAsync();
        if (cancelled || !last) return;
        // Deliberately not added to the cluster: it may be minutes old and from
        // the car park, and averaging it with real fixes would poison them.
        setState((current) =>
          current.position ? current : { position: rawPosition(last), status: 'tracking' }
        );
      } catch {
        if (!cancelled) setState({ position: null, status: 'unavailable' });
      }
    };

    void begin();

    return () => {
      cancelled = true;
      stopGnssWatch();
      // expo-location's web subscription calls `LocationEventEmitter.removeSubscription`,
      // which React Native removed from its EventEmitter API — so `remove()` throws in a
      // browser. Unmounting must not take the whole screen down with it, and there is
      // nothing to recover: the watch is being discarded either way.
      try {
        subscription?.remove();
      } catch {
        // ignore
      }
      subscription = null;
    };
  }, [enabled]);

  return state;
};
